import React, { useId, useRef } from "react";
import type { InputSize } from "./Input";
import "./inputs.scss";

export interface OtpInputProps {
	length?: number;
	label?: string;
	size?: InputSize;
	value?: string;
	onChange?: (value: string) => void;
	disabled?: boolean;
	invalid?: boolean;
	hint?: string;
	errorMessage?: string;
}

export const OtpInput: React.FC<OtpInputProps> = ({
	length = 6,
	label = "Código OTP",
	size = "md",
	value = "",
	onChange,
	disabled = false,
	invalid = false,
	hint,
	errorMessage,
}) => {
	const baseId = useId();
	const refs = useRef<Array<HTMLInputElement | null>>([]);
	const digits = Array.from({ length }, (_, i) => value[i] ?? "");
	const isInvalid = invalid || Boolean(errorMessage);

	const emitChange = (nextDigits: string[]) => {
		onChange?.(nextDigits.join("").slice(0, length));
	};

	const handleChange = (index: number, char: string) => {
		const sanitized = char.replace(/\D/g, "").slice(-1);
		const next = [...digits];
		next[index] = sanitized;
		emitChange(next);
		if (sanitized && index < length - 1) {
			refs.current[index + 1]?.focus();
		}
	};

	const handleKeyDown = (
		index: number,
		event: React.KeyboardEvent<HTMLInputElement>
	) => {
		if (event.key === "Backspace" && !digits[index] && index > 0) {
			refs.current[index - 1]?.focus();
		}
		if (event.key === "ArrowLeft" && index > 0) {
			refs.current[index - 1]?.focus();
		}
		if (event.key === "ArrowRight" && index < length - 1) {
			refs.current[index + 1]?.focus();
		}
	};

	const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
		event.preventDefault();
		const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
		if (!pasted) return;
		emitChange(pasted.split(""));
		const focusIndex = Math.min(pasted.length, length - 1);
		refs.current[focusIndex]?.focus();
	};

	return (
		<div className={["ds-otp", size !== "md" ? `ds-otp--${size}` : ""].filter(Boolean).join(" ")}>
			{label ? <p className="ds-otp__label">{label}</p> : null}
			<div className="ds-otp__fields" role="group" aria-label={label}>
				{digits.map((digit, index) => (
					<input
						key={`${baseId}-${index}`}
						ref={(el) => {
							refs.current[index] = el;
						}}
						className={[
							"ds-otp__cell",
							isInvalid ? "ds-otp__cell--invalid" : "",
						]
							.filter(Boolean)
							.join(" ")}
						type="text"
						inputMode="numeric"
						autoComplete={index === 0 ? "one-time-code" : "off"}
						maxLength={1}
						value={digit}
						disabled={disabled}
						aria-invalid={isInvalid || undefined}
						onChange={(e) => handleChange(index, e.target.value)}
						onKeyDown={(e) => handleKeyDown(index, e)}
						onPaste={handlePaste}
					/>
				))}
			</div>
			{errorMessage ? (
				<p className="ds-input__error" role="alert">
					{errorMessage}
				</p>
			) : hint ? (
				<p className="ds-input__hint">{hint}</p>
			) : null}
		</div>
	);
};
